async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  // Only declare a JSON content-type when we actually have a JSON body —
  // otherwise Fastify rejects with "Body cannot be empty when content-type
  // is set to 'application/json'" for empty POSTs.
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let payload: unknown = null;
    try { payload = await res.json(); } catch {}
    const message =
      (payload && typeof payload === 'object' && 'error' in payload && typeof (payload as { error: unknown }).error === 'string')
        ? (payload as { error: string }).error
        : `request_failed_${res.status}`;
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  delete: <T>(url: string) => request<T>('DELETE', url),
};

export interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    hasPassword: boolean;
    /** True for instant /optin funnel sessions — free course only, can't set a password. */
    soft: boolean;
  } | null;
}

export interface CourseModule {
  id: string;
  title: string;
  description: string;
  orderIndex: number;
  /** Header image; null when none was uploaded — the SPA draws a gradient. */
  imageUrl: string | null;
  lessons: CourseLesson[];
}

export interface CourseLesson {
  id: string;
  title: string;
  description: string;
  aiSummary: string;
  durationSeconds: number;
  orderIndex: number;
  thumbnailUrl: string | null;
  resources: Array<{ id: string; kind: 'pdf' | 'link'; label: string; url: string }>;
  bookmarked: boolean;
  progress: {
    furthestSeconds: number;
    lastPositionSeconds: number;
    completedAt: number;
    completed: boolean;
  };
}

export interface Bookmark {
  lessonId: string;
  lessonTitle: string;
  durationSeconds: number;
  thumbnailUrl: string | null;
  moduleId: string;
  moduleTitle: string;
  courseSlug: string;
  courseTitle: string;
  /** Reserved for timestamp bookmarks; always 0 today. */
  seconds: number;
  completed: boolean;
  createdAt: number;
}

/** Bookmarks whose course is no longer accessible are omitted server-side. */
export interface BookmarksResponse {
  bookmarks: Bookmark[];
}

export interface ResumeLesson {
  lessonId: string;
  lessonTitle: string;
  durationSeconds: number;
  lastPositionSeconds: number;
  thumbnailUrl: string | null;
  moduleId: string;
  courseSlug: string;
  courseTitle: string;
}

/** `resume` is null when the user has nothing unfinished — the new-user case. */
export interface ContinueResponse {
  resume: ResumeLesson | null;
}

export interface LessonChapter { time: number; title: string }
export interface PlaybackResponse {
  playbackUrl: string;
  iframeUrl: string;
  durationSeconds: number;
  chapters: LessonChapter[];
  ctaUrl: string;
  ctaLabel: string;
  subtitlesVtt: string;
  language: string;
}

export interface SearchResult {
  lessonId: string;
  lessonTitle: string;
  moduleId: string;
  moduleTitle: string;
  startSeconds: number;
  snippet: string;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
}

export interface CourseListItem {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  thumbnailUrl: string;
  ctaUrl: string;
  orderIndex: number;
  featured: boolean;
  hasAccess: boolean;
  /** Unix seconds when access expires; 0 = never. */
  accessExpiresAt: number;
  totalLessons: number;
  completedLessons: number;
}

export interface BonusCard {
  enabled: true;
  title: string;
  thumbnailUrl: string;
  ctaUrl: string;
  requiredLessons: number;
  completedLessons: number;
  unlocked: boolean;
}

export interface CoursesResponse {
  courses: CourseListItem[];
  bonus: BonusCard | null;
}

export interface CourseDetail {
  course: {
    id: string;
    slug: string;
    title: string;
    subtitle: string;
    description: string;
    thumbnailUrl: string;
    ctaUrl: string;
    /** Drip course: each lesson unlocks only once the previous one is completed. */
    sequentialUnlock: boolean;
    /** This course gates the bonus → its last lesson shows a "Bonus freischalten" CTA. */
    bonusUnlock: boolean;
  };
  modules: CourseModule[];
}
